import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { getApplicationConfiguration } from '@/api/services/applicationConfiguration';
import { accountSessionApi } from '@/api/services/accountSession';
import { tokenStorage } from '@/auth/tokenStorage';
import { tenantStore } from '@/tenant/tenantStore';
import { setOnAuthFailure } from '@/api/client/httpClient';
import type { ApplicationConfiguration, CurrentUser } from '@/api/types/abp';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: Status;
  user: CurrentUser | null;
  grantedPolicies: Record<string, boolean>;
  tenantName: string | null;
  tenantId: string | null;
  // Call after a successful login/2FA to load the session profile.
  completeLogin: () => Promise<void>;
  logout: () => Promise<void>;
  // True while a "Login as" session is active (the impersonator's own session is
  // parked). returnToSelf restores it without a fresh login.
  isImpersonating: boolean;
  returnToSelf: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [config, setConfig] = useState<ApplicationConfiguration | null>(null);
  const mounted = useRef(true);

  const loadProfile = useCallback(async () => {
    const cfg = await getApplicationConfiguration();
    if (!mounted.current) return;
    setConfig(cfg);
    setStatus(cfg.currentUser.isAuthenticated ? 'authenticated' : 'unauthenticated');
  }, []);

  const clearSession = useCallback(() => {
    tokenStorage.clear();
    // Drop the backend-set `__tenant` cookie so it can't force the wrong tenant
    // on the next sign-in (esp. host). Auth is header-based.
    tenantStore.syncCookie();
    setConfig(null);
    setStatus('unauthenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await accountSessionApi.logout();
    } catch {
      /* best-effort; clear locally regardless */
    }
    clearSession();
  }, [clearSession]);

  const completeLogin = useCallback(async () => {
    setStatus('loading');
    await loadProfile();
  }, [loadProfile]);

  // Restore the parked impersonator session (set by the "Login as" flow) so the
  // original user returns to their own account without signing in again.
  const returnToSelf = useCallback(async () => {
    const stash = tokenStorage.impersonatorStash();
    if (!stash) return;
    tokenStorage.save(stash);
    tokenStorage.clearImpersonatorStash();
    setStatus('loading');
    await loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    mounted.current = true;
    // On hard refresh: recover the session if a token survived in storage.
    if (tokenStorage.accessToken()) {
      loadProfile().catch(() => clearSession());
    } else {
      setStatus('unauthenticated');
    }
    return () => {
      mounted.current = false;
    };
  }, [loadProfile, clearSession]);

  useEffect(() => {
    // When the client exhausts refresh on a 401, drop to the login screen.
    setOnAuthFailure(() => clearSession());
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: config?.currentUser ?? null,
      grantedPolicies: config?.auth.grantedPolicies ?? {},
      tenantName: config?.currentTenant.name ?? null,
      tenantId: config?.currentTenant.id ?? null,
      completeLogin,
      logout,
      // Authoritative signal = the backend impersonation claim on the current
      // session (same as the ImpersonationBanner), NOT the local stash — a stash
      // can linger from a past session, but the claim only exists on a token that
      // was actually issued by the impersonation grant.
      isImpersonating: !!config?.currentUser?.impersonatorUserId,
      returnToSelf,
    }),
    [status, config, completeLogin, logout, returnToSelf],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
